import classNames from "classnames";
import { produce } from "immer";
import React, { useEffect, useRef, useState } from "react";
import { Key } from "ts-key-enum";
import { t } from "ttag";

import { Form } from "../../../forms/Form";
import { FormInput } from "../../../forms/FormInput";
import { FormPhoneNumber } from "../../../forms/FormPhoneNumber";
import { FormSelect } from "../../../forms/FormSelect";
import { FormSubmit } from "../../../forms/FormSubmit";
import { NonFieldErrors } from "../../../forms/NonFieldErrors";
import { strings } from "../../../localization";
import {
    IAddress,
    IAddressCountry,
    IAddressState,
} from "../../../models/address";
import { objectKeys } from "../../../utils/functional";
import { focusElement } from "../../../utils/keyboardFocus";
import { newAutocomplete } from "../../../utils/maps";
import { updateEnteredLocation } from "../../common/reducers";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { AddressSuggestionModal } from "../components/AddressSuggestionModal";
import { AddressWarningModal } from "../components/AddressWarningModal";
import { SavedAddressSelector } from "../components/SavedAddressSelector";
import { Step } from "../constants";
import { updateAddressFieldWithMatching } from "../helper-utils";
import { setFields } from "../reducers";
import { IReduxFormState } from "../reducers.interfaces";
import { CheckoutStep } from "./CheckoutStepContainer";
import { ShippingAddressSummary } from "./ShippingAddressSummary";

interface IErrors {
    non_field_errors?: string[];
    guest_email?: string[];
    first_name?: string[];
    last_name?: string[];
    line1?: string[];
    line2?: string[];
    line4?: string[];
    state?: string[];
    postcode?: string[];
    country?: string[];
    phone_number?: string[];
}

// Helper functions moved outside component
const parseAutocompletePlace = (
    place: google.maps.GeocoderAddressComponent[],
): {
    line1: string;
    postcode: string;
    line4: string;
    state: string;
} => {
    const placeData: { [s: string]: string } = {};
    const components: { [s: string]: "long_name" | "short_name" } = {
        street_number: "short_name",
        route: "short_name",
        postal_code: "short_name",
        locality: "long_name",
        administrative_area_level_1: "short_name",
    };

    place.forEach((component) => {
        const type = component.types[0];
        const prop: "long_name" | "short_name" =
            type in components ? components[type] : "long_name";
        const value = component[prop];
        placeData[type] = value;
    });

    const line1Parts = [placeData.street_number, placeData.route].filter(
        (part) => part,
    );
    return {
        line1: line1Parts.join(" "),
        postcode: placeData.postal_code || "",
        line4: placeData.sublocality_level_1 || placeData.locality || "",
        state: placeData.administrative_area_level_1 || "",
    };
};

const addressesAreSame = (addr: IAddress, form: IReduxFormState): boolean => {
    return (
        addr.line1 === form.shipping_line1 &&
        addr.line2 === form.shipping_line2 &&
        addr.line4 === form.shipping_line4 &&
        addr.state === form.shipping_state &&
        addr.postcode === form.shipping_postcode
    );
};

const countClientSideErrors = (errors: IErrors): number => {
    return objectKeys(errors)
        .filter((field) => field !== "non_field_errors")
        .reduce((memo, field) => {
            const fieldErrors = errors[field];
            const numErrors = fieldErrors ? fieldErrors.length : 0;
            return memo + numErrors;
        }, 0);
};

const blockEnterKeySubmit = (
    event: React.KeyboardEvent<HTMLInputElement>,
): void => {
    if (event.key === (Key.Enter as string)) {
        event.preventDefault();
    }
};

const getCountryOptions = (
    countries: readonly IAddressCountry[],
): { value: string; label: string }[] => {
    return countries.map((country) => ({
        value: country.url,
        label: country.printable_name,
    }));
};

const getStateOptions = (
    states: readonly IAddressState[],
): { value: string; label: string }[] => {
    const options = states.map((state) => ({
        value: state.code,
        label: state.name,
    }));
    options.unshift({
        value: "",
        label: "",
    });
    return options;
};

export const ShippingAddress = (props: {
    heading: string;
    errors: string[];
    isCurrentStep: boolean;
    saveEmailAddress: () => Promise<{ owner_email: string }>;
    saveShippingAddress: (bypassUSPSValidation: boolean) => Promise<IAddress>;
    onContinue: (event?: React.MouseEvent<HTMLElement>) => void;
    onEdit: (event?: React.MouseEvent<HTMLElement>) => void;
    formRef?: React.RefObject<HTMLFormElement | null>;
}): React.ReactNode => {
    // Redux state and dispatch
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.common.user);
    const form = useAppSelector((state) => state.checkout.form);
    const countries = useAppSelector((state) => state.checkout.data.countries);
    const states = useAppSelector(
        (state) => state.checkout.data.billing_states,
    );
    const savedAddresses = useAppSelector(
        (state) => state.checkout.data.addresses,
    );
    const shippingMethodErrors = useAppSelector(
        (state) => state.checkout.data.shipping_method_errors,
    );
    const showForm = useAppSelector(
        (state) => state.checkout.form.current_step === Step.SHIPPING_ADDRESS,
    );

    // Component state
    const [isSaving, setIsSaving] = useState(false);
    const [showErrorMessages, setShowErrorMessages] = useState(false);
    const [showAddressWarningModal, setShowAddressWarningModal] =
        useState(false);
    const [showAddressSuggestionModal, setShowAddressSuggestionModal] =
        useState(false);
    const [suggestedAddress, setSuggestedAddress] = useState<IAddress | null>(
        null,
    );
    const [errors, setErrors] = useState<IErrors>({});

    // Refs
    const autocompleteRef = useRef<HTMLInputElement>(null);
    const submitButtonRef = useRef<HTMLInputElement>(null);
    const formRef = useRef(form);
    const savedAddressesRef = useRef(savedAddresses);

    useEffect(() => {
        formRef.current = form;
    }, [form]);

    useEffect(() => {
        savedAddressesRef.current = savedAddresses;
    }, [savedAddresses]);

    // Setup Google Maps autocomplete
    // Note: Only depend on dispatch to avoid recreating autocomplete on every form change
    // The listener will capture the latest form/savedAddresses via closure
    useEffect(() => {
        const inputElem = autocompleteRef.current;
        if (!inputElem) return;

        const autocomplete = newAutocomplete(inputElem);
        const listener = autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            if (place.address_components) {
                const parsed = parseAutocompletePlace(place.address_components);
                // Access latest form and savedAddresses from Redux store directly
                // to avoid stale closure while keeping effect dependencies minimal
                const latestForm = formRef.current;
                const latestSavedAddresses = savedAddressesRef.current;
                const fields = updateAddressFieldWithMatching(
                    latestForm,
                    latestSavedAddresses as readonly IAddress[],
                    {
                        shipping_line1:
                            parsed.line1 || latestForm.shipping_line1,
                        shipping_postcode:
                            parsed.postcode || latestForm.shipping_postcode,
                        shipping_line4:
                            parsed.line4 || latestForm.shipping_line4,
                        shipping_state:
                            parsed.state || latestForm.shipping_state,
                    },
                );
                dispatch(setFields(fields));
            }
        });

        return () => {
            if ("remove" in listener && typeof listener.remove === "function") {
                listener.remove();
            }
        };
    }, [dispatch]); // Only re-create if dispatch changes (which should be stable)

    const onFieldChange = (
        event:
            | React.FormEvent<HTMLInputElement>
            | React.FormEvent<HTMLSelectElement>,
    ): void => {
        const fieldValue =
            event.currentTarget.type === "checkbox"
                ? (event.currentTarget as HTMLInputElement).checked
                : event.currentTarget.value;
        const fields = updateAddressFieldWithMatching(
            form,
            savedAddresses as readonly IAddress[],
            {
                [event.currentTarget.name]: fieldValue,
            },
        );
        dispatch(setFields(fields));
    };

    const onValidStateChange = (
        fieldName: string,
        errorMessages: string[],
    ): void => {
        const cleanFieldName = fieldName.replace(
            "shipping_",
            "",
        ) as keyof IErrors;
        setErrors((oldErrs) =>
            produce(oldErrs, (draft) => {
                draft[cleanFieldName] = errorMessages;
            }),
        );
    };

    const saveAddress = async (
        bypassUSPSValidation: boolean,
    ): Promise<void> => {
        if (shippingMethodErrors.length > 0) {
            return;
        }

        const gotoNextStep = (): void => {
            setIsSaving(false);
            setErrors({});
            setShowErrorMessages(false);
            setShowAddressWarningModal(false);
            setShowAddressSuggestionModal(false);
            dispatch(
                updateEnteredLocation({
                    formatted_address: form.shipping_postcode.slice(0, 5),
                    formatted_address_long: `${form.shipping_line4}, ${form.shipping_state}`,
                    zip: form.shipping_postcode.slice(0, 5),
                }),
            );
            props.onContinue();
        };

        const showSuggestionModal = (addr: IAddress): void => {
            setShowAddressSuggestionModal(true);
            setSuggestedAddress(addr);
        };

        const showWarningModal = (): void => {
            setShowAddressWarningModal(true);
        };

        const displayInlineErrors = (errorData: {
            [s: string]: string;
        }): void => {
            setIsSaving(false);
            setErrors(errorData);
            setShowErrorMessages(true);
        };

        const abortSave = (): void => {
            setIsSaving(false);
            setShowErrorMessages(true);
        };

        setIsSaving(true);
        setErrors({});
        setShowAddressWarningModal(false);
        setShowAddressSuggestionModal(false);

        try {
            await props.saveEmailAddress();
            const addr = await props.saveShippingAddress(bypassUSPSValidation);
            if (shippingMethodErrors.length > 0) {
                abortSave();
            } else if (bypassUSPSValidation || addressesAreSame(addr, form)) {
                gotoNextStep();
            } else {
                showSuggestionModal(addr);
            }
        } catch (err: unknown) {
            if (shippingMethodErrors.length > 0) {
                abortSave();
            } else if (
                err &&
                typeof err === "object" &&
                "response" in err &&
                err.response &&
                typeof err.response === "object" &&
                "body" in err.response &&
                err.response.body &&
                typeof err.response.body === "object"
            ) {
                const body = err.response.body as Record<string, unknown>;
                if (
                    Object.keys(body).length === 1 &&
                    "non_field_errors" in body
                ) {
                    showWarningModal();
                } else {
                    displayInlineErrors(body as { [s: string]: string });
                }
            }
        }
    };

    const onFormSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        const errorCount = countClientSideErrors(errors);
        if (errorCount > 0) {
            setShowErrorMessages(true);
            return;
        }
        return saveAddress(false);
    };

    const onAddressWarningContinue = (): void => {
        void saveAddress(true);
    };

    const onAddressWarningEdit = (): void => {
        setIsSaving(false);
        setShowAddressWarningModal(false);
    };

    const onAddressSuggestionContinue = (shouldImport: boolean): void => {
        if (shouldImport && suggestedAddress) {
            // Import suggested shipping address fields
            dispatch(
                setFields({
                    shipping_line1: suggestedAddress.line1,
                    shipping_line2: suggestedAddress.line2,
                    shipping_line4: suggestedAddress.line4,
                    shipping_state: suggestedAddress.state,
                    shipping_postcode: suggestedAddress.postcode,
                }),
            );
        }
        void saveAddress(true);
    };

    const closeSuggestionModal = (): void => {
        setShowAddressSuggestionModal(false);
        setIsSaving(false);
    };

    const focusOnSubmitButton = (): void => {
        if (submitButtonRef.current) {
            focusElement(submitButtonRef.current);
        }
    };

    const bodyClasses = classNames({
        "checkout-step__body": true,
        "checkout-step__body--collapsed": !showForm,
    });

    let emailField: React.JSX.Element | null = null;
    if (!user || !user.email || user.is_csr) {
        emailField = (
            <div>
                <FormInput
                    autoComplete="email"
                    name="guest_email"
                    id="guest_email"
                    label={t`Email Address`}
                    labelPlacement="dynamic"
                    value={form.guest_email}
                    errors={errors.guest_email}
                    validation={["required", "email"]}
                    onChange={onFieldChange}
                    disabled={isSaving}
                    onValidStateChange={onValidStateChange}
                    showErrorMessages={showErrorMessages}
                />
            </div>
        );
    }

    const countryOptions = getCountryOptions(
        countries as readonly IAddressCountry[],
    );
    const stateOptions = getStateOptions(states as readonly IAddressState[]);

    return (
        <CheckoutStep
            className="checkout-step--shipping-address form"
            heading={props.heading}
            isCurrentStep={props.isCurrentStep}
        >
            <div className={bodyClasses}>
                {/* TODO(i18n): Wrap in t tagged template: <p>{t`Please ensure the information you provide is correct, because we will share this information with our trusted shipping agent(s) to deliver your order.`}</p> */}
                <p>
                    Please ensure the information you provide is correct,
                    because we will share this information with our trusted
                    shipping agent(s) to deliver your order.
                </p>
                <Form onSubmit={onFormSubmit} ref={props.formRef}>
                    <NonFieldErrors
                        errors={errors.non_field_errors}
                        showErrorMessages={true}
                    />
                    <NonFieldErrors
                        errors={props.errors.concat(shippingMethodErrors)}
                        showErrorMessages={true}
                    />
                    <SavedAddressSelector
                        prefix="shipping"
                        isDisabled={isSaving}
                    />
                    <FormInput
                        type="text"
                        id="shipping_first_name"
                        name="shipping_first_name"
                        label={t`First Name`}
                        labelPlacement="dynamic"
                        value={form.shipping_first_name}
                        errors={errors.first_name}
                        validation={["required", "firstname-empty"]}
                        maxLength={25}
                        autoComplete="given-name"
                        onChange={onFieldChange}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                    />
                    <FormInput
                        type="text"
                        id="shipping_last_name"
                        name="shipping_last_name"
                        label={t`Last Name`}
                        labelPlacement="dynamic"
                        value={form.shipping_last_name}
                        errors={errors.last_name}
                        validation={["required", "lastname-empty"]}
                        maxLength={25}
                        autoComplete="family-name"
                        onChange={onFieldChange}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                    />
                    <FormInput
                        inputRef={autocompleteRef}
                        type="text"
                        id="shipping_line1"
                        name="shipping_line1"
                        label={t`Street and number, c/o.`}
                        labelPlacement="dynamic"
                        adaText={t`Begin typing to search, use arrow keys to navigate, Enter to select`}
                        placeholder=""
                        value={form.shipping_line1}
                        errors={errors.line1}
                        validation={["required", "shipping-address-empty"]}
                        maxLength={25}
                        maxBytes={240}
                        autoComplete="address-line1"
                        onChange={onFieldChange}
                        onKeyPress={blockEnterKeySubmit}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                    />
                    <FormInput
                        type="text"
                        id="shipping_line2"
                        name="shipping_line2"
                        label={t`Apartment, suite, unit, etc. (Optional)`}
                        labelPlacement="dynamic"
                        autoComplete="address-line2"
                        value={form.shipping_line2}
                        maxLength={25}
                        maxBytes={240}
                        errors={errors.line2}
                        onChange={onFieldChange}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                    />
                    <FormInput
                        type="text"
                        id="shipping_line4"
                        name="shipping_line4"
                        label={t`City`}
                        labelPlacement="dynamic"
                        autoComplete="address-level2"
                        value={form.shipping_line4}
                        errors={errors.line4}
                        validation={["required", "city-empty"]}
                        maxLength={60}
                        maxBytes={60}
                        onChange={onFieldChange}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                    />
                    <FormSelect
                        label={t`State`}
                        labelPlacement="dynamic"
                        id="shipping_state"
                        name="shipping_state"
                        value={form.shipping_state}
                        errors={errors.state}
                        validation={["required", "state-empty"]}
                        autoComplete="address-level1"
                        onChange={onFieldChange}
                        choices={stateOptions}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                    />
                    <FormInput
                        type="text"
                        id="shipping_postcode"
                        name="shipping_postcode"
                        label={t`Zip Code`}
                        labelPlacement="dynamic"
                        value={form.shipping_postcode}
                        errors={errors.postcode}
                        validation={["required", "zipcode"]}
                        maxLength={10}
                        maxBytes={60}
                        autoComplete="postal-code"
                        onChange={onFieldChange}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                    />
                    <FormSelect
                        label={t`Country`}
                        labelPlacement="dynamic"
                        id="shipping_country"
                        name="shipping_country"
                        value={form.shipping_country}
                        errors={errors.country}
                        onChange={onFieldChange}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                        validation="required"
                        autoComplete="country"
                        choices={countryOptions}
                    />
                    <FormPhoneNumber
                        type="phone"
                        id="shipping_phone_number"
                        name="shipping_phone_number"
                        label={t`Phone Number`}
                        labelPlacement="dynamic"
                        value={form.shipping_phone_number}
                        errors={errors.phone_number}
                        validation={["required", "phone"]}
                        autoComplete="tel"
                        onChange={onFieldChange}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                    />
                    {emailField}
                    <FormSubmit
                        className="button button--full-width button--continue-to-delivery"
                        inputRef={submitButtonRef}
                        // TODO(i18n): Wrap fallback string in t tagged template: strings.get("CONTINUE_BTN_LABEL_SHIPPING_ADDRESS") || t`Continue`
                        value={
                            strings.get(
                                "CONTINUE_BTN_LABEL_SHIPPING_ADDRESS",
                            ) || "Continue"
                        }
                        disabled={isSaving || shippingMethodErrors.length > 0}
                    />
                </Form>
            </div>
            <ShippingAddressSummary onEdit={props.onEdit} />
            <AddressWarningModal
                form={form}
                onContinue={onAddressWarningContinue}
                onEdit={onAddressWarningEdit}
                showAddressWarningModal={showAddressWarningModal}
                addressPrefix={"shipping"}
            />
            <AddressSuggestionModal
                form={form}
                suggestedAddress={suggestedAddress}
                onContinue={onAddressSuggestionContinue}
                onRequestClose={closeSuggestionModal}
                onAfterClose={focusOnSubmitButton}
                showAddressSuggestionModal={showAddressSuggestionModal}
                addressPrefix={"shipping"}
            />
        </CheckoutStep>
    );
};
